import { useCallback, useEffect, useState } from "react";
import { useTriggerEventTracker } from "../../shared/PersistentSummary/helpers/PersistentPaymentSummaryHelper";

const getCheckbox = (
  containerSelector: string,
  fieldName: string,
): HTMLInputElement | null => {
  return document.querySelector<HTMLInputElement>(
    `${containerSelector} input[type=checkbox][name="${fieldName}"]`,
  );
};

const getCheckboxState = (
  containerSelector: string,
  fieldName: string,
): boolean => {
  const checkbox = getCheckbox(containerSelector, fieldName);
  return checkbox?.checked ?? false;
};

const toggleCheckbox = (
  containerSelector: string,
  fieldName: string,
): boolean | null => {
  const checkbox = getCheckbox(containerSelector, fieldName);
  if (!checkbox) {
    return null;
  }
  checkbox.click();
  return checkbox.checked;
};

type UseDialogueModalProps = {
  fieldName: string;
  checkboxContainer: string;
  onOpen?: (fieldName: string) => void;
  onCoverageAdd?: (fieldName: string) => void;
  onCoverageRemove?: (fieldName: string) => void;
};

export const useDialogueModal = ({
  fieldName,
  checkboxContainer,
  onOpen,
  onCoverageAdd,
  onCoverageRemove,
}: UseDialogueModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCoverageAdded, setIsCoverageAdded] = useState(false);
  const [eventDetails, setEventDetails] = useState<{
    eventName: string | null;
    args: string[];
  }>({ eventName: null, args: [] });

  useTriggerEventTracker(eventDetails.eventName, ...eventDetails.args);

  const syncCheckboxState = useCallback(() => {
    setIsCoverageAdded(getCheckboxState(checkboxContainer, fieldName));
  }, [checkboxContainer, fieldName]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleCtaClick = useCallback(() => {
    const newState = toggleCheckbox(checkboxContainer, fieldName);
    if (newState !== null) {
      setIsCoverageAdded(newState);
      const eventName = newState
        ? "coverage_selection_recommendations_coverage_added"
        : "coverage_selection_recommendations_coverage_removed";
      setEventDetails({ eventName, args: [fieldName] });
      if (newState) {
        onCoverageAdd?.(fieldName);
      } else {
        onCoverageRemove?.(fieldName);
      }
    }
    setIsOpen(false);
  }, [checkboxContainer, fieldName, onCoverageAdd, onCoverageRemove]);

  const handleTriggerClick = useCallback(() => {
    setIsOpen(true);
    setEventDetails({
      eventName: "coverage_selection_learn_more_modal_opened",
      args: [fieldName.replace(/_coverage$/, "")],
    });
    onOpen?.(fieldName);
  }, [fieldName, onOpen]);

  useEffect(() => {
    syncCheckboxState();

    const container = document.querySelector(checkboxContainer);
    if (!container) return;

    const handleChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.type === "checkbox" && target.name === fieldName) {
        syncCheckboxState();
      }
    };

    container.addEventListener("change", handleChange);

    return () => {
      if (container) {
        container.removeEventListener("change", handleChange);
      }
    };
  }, [checkboxContainer, fieldName, syncCheckboxState]);

  return {
    isOpen,
    isCoverageAdded,
    handleClose,
    handleCtaClick,
    handleTriggerClick,
  };
};
